// The one shared tab control for the whole app (D-16, D-17).
//
// Chrome: Option B's segmented control (grey track, raised active pill) with
// Option E's layout rule (content-sized tabs that wrap instead of scrolling).
// Visual spec: docs/plans/active/tab-restyle-options.html — the `CHOSEN` section.
//
// Three non-negotiable properties, each a stated requirement rather than
// incidental styling:
//   • `flex-wrap` with no `overflow-x` → the stray scrollbar cannot appear and
//     no tab is ever clipped.
//   • `shrink-0` (not `flex-1`) → tabs are content-sized, per the user's
//     correction to Option B.
//   • `min-h-[44px]` → satisfies UX standard rule 5's coarse-pointer target
//     minimum.
//
// Replaces all 10 previously hand-rolled tab strips (6 page-level, 4
// modal-level). A single component is the only thing that stops the style
// drifting back.

import { useRef, type KeyboardEvent } from "react";

export type TabItem<T extends string> = {
  id: T;
  label: string;
  /** Rendered as a dimmed suffix, e.g. "Active 3". Omit for no badge. */
  count?: number;
};

export function TabBar<T extends string>({
  tabs,
  active,
  onSelect,
  className,
  ariaLabel,
}: {
  tabs: readonly TabItem<T>[];
  active: T;
  onSelect: (id: T) => void;
  /** Extra classes on the outer track, e.g. "print:hidden mb-4". */
  className?: string;
  ariaLabel?: string;
}): JSX.Element {
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Standard tablist keyboard behaviour (automatic activation): Left/Right move
  // the active tab and follow focus. None of the 10 strips this replaces had it;
  // it is cheap to add once here.
  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const dir = e.key === "ArrowRight" ? 1 : -1;
    const next = (index + dir + tabs.length) % tabs.length;
    onSelect(tabs[next].id);
    btnRefs.current[next]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={
        "flex flex-wrap gap-0.5 rounded-xl bg-gray-100 p-1 dark:bg-gray-800 " +
        (className ?? "")
      }
    >
      {tabs.map((t, i) => (
        <button
          key={t.id}
          ref={(el) => {
            btnRefs.current[i] = el;
          }}
          role="tab"
          type="button"
          aria-selected={t.id === active}
          tabIndex={t.id === active ? 0 : -1}
          onClick={() => onSelect(t.id)}
          onKeyDown={(e) => onKeyDown(e, i)}
          className={
            "shrink-0 rounded-lg px-3.5 py-2 text-[13px] font-semibold whitespace-nowrap " +
            "min-h-[44px] transition-all " +
            (t.id === active
              ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white"
              : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200")
          }
        >
          {t.label}
          {t.count !== undefined && (
            <span
              className={
                t.id === active ? "ml-1.5 text-gray-400" : "ml-1.5 text-gray-400/70"
              }
            >
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
